import {
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { dashboardKeys } from "@/lib/api/dashboard";
import type {
  ClaimGuildResponse,
  ClaimGuildError,
  GuildPermissionsMeResponse,
  GuildPermissionsAllResponse,
  UpdateGuildPermissionsPayload,
  AuditLogResponse,
  GuildOverviewResponse,
} from "@/lib/api/types";
import { AxiosError } from "axios";

export interface ClaimGuildParams {
  discordGuildId: string;
  applicationId: string;
}

export async function createGuildAddIntent(
  applicationId: string,
  discordGuildId: string
) {
  return apiClient
    .post("/api/dashboard/guilds/add-intent", {
      applicationId,
      discordGuildId,
    })
    .then((r) => r.data);
}

export function invalidateGuildData(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: dashboardKeys.guilds() });
  queryClient.invalidateQueries({ queryKey: dashboardKeys.me() });
}

export function useClaimGuild() {
  const queryClient = useQueryClient();

  return useMutation<
    ClaimGuildResponse,
    AxiosError<ClaimGuildError>,
    ClaimGuildParams
  >({
    mutationFn: ({ discordGuildId, applicationId }) =>
      apiClient
        .post<ClaimGuildResponse>(`/api/guilds/${discordGuildId}/claim`, {
          applicationId,
        })
        .then((r) => r.data),
    onSuccess: () => {
      invalidateGuildData(queryClient);
    },
  });
}

export function isClaimGuildError(
  error: unknown
): error is AxiosError<ClaimGuildError> {
  return (
    error instanceof AxiosError &&
    typeof error.response?.data === "object" &&
    error.response?.data !== null &&
    "code" in error.response.data
  );
}

export function guildPermissionsMeQueryOptions(applicationGuildId: string) {
  return queryOptions({
    queryKey: ["guild-permissions-me", applicationGuildId],
    queryFn: () =>
      apiClient
        .get<GuildPermissionsMeResponse>(
          `/api/guilds/${applicationGuildId}/permissions/me`
        )
        .then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useGuildPermissionsAll(applicationGuildId: string) {
  return useQuery({
    queryKey: ["guild-permissions", applicationGuildId],
    queryFn: () =>
      apiClient
        .get<GuildPermissionsAllResponse>(
          `/api/guilds/${applicationGuildId}/permissions`
        )
        .then((r) => r.data),
  });
}

export function useUpdateGuildPermissions(applicationGuildId: string) {
  return useMutation({
    mutationFn: ({
      userId,
      permissions,
    }: {
      userId: string;
      permissions: UpdateGuildPermissionsPayload;
    }) =>
      apiClient
        .patch(
          `/api/guilds/${applicationGuildId}/permissions/${userId}`,
          permissions
        )
        .then((r) => r.data),
  });
}

export const guildOverviewQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: ["guild-overview", applicationGuildId],
    queryFn: () =>
      apiClient
        .get<GuildOverviewResponse>(
          `/api/guilds/${applicationGuildId}/overview`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
    staleTime: 60_000,
  });

export function useGuildOverview(applicationGuildId: string) {
  return useQuery(guildOverviewQueryOptions(applicationGuildId));
}

export function useGuildAuditLog(applicationGuildId: string, enabled = true) {
  return useQuery({
    queryKey: ["guild-audit-log", applicationGuildId],
    queryFn: () =>
      apiClient
        .get<AuditLogResponse>(
          `/api/guilds/${applicationGuildId}/permissions/history`
        )
        .then((r) => r.data),
    enabled: enabled && !!applicationGuildId,
  });
}
